import { useMemo, useState } from "react";
import { CtPayment } from "src/api/contract/contract-types";
import { BaseButton, BaseInput, BaseTooltip } from "src/components";
import { BaseRadio } from "src/components/BaseRadio";
import { useModal } from "src/recoil/modalState/hook";
import { numberToStringWithComma } from "src/utils";

type CancelType = "full" | "partial";

interface Props {
  paymentInfo: CtPayment;
  onClose: () => void;
  onPaymentCancel: ({ paymentId, cancelAmount }: { paymentId: string; cancelAmount: string }) => void;
}

const CreditCardPaymentCancelModal = ({ paymentInfo, onClose, onPaymentCancel }: Props) => {
  const { setBaseModal } = useModal();
  const [cancelType, setCancelType] = useState<CancelType>("full");
  const [partialAmount, setPartialAmount] = useState<string>("");
  const [error, setError] = useState<string>("");

  // 결제 잔액 계산 (전체 금액 - 이미 취소된 금액)
  const remainingAmount = useMemo(() => {
    let total = paymentInfo.totalAmount;
    if (paymentInfo.paymentCancel) {
      total -= paymentInfo.paymentCancel.cancelAmount;
    }
    return total;
  }, [paymentInfo]);

  const handleConfirm = (cancelAmount: string) => {
    if (cancelType === "partial") {
      const amount = Number(partialAmount);
      if (!partialAmount || amount <= 0) {
        setError("부분취소 금액을 입력해주세요.");
        return;
      }

      if (amount > remainingAmount) {
        setError(`부분취소 금액은 결제 잔액을 초과할 수 없습니다.`);
        return;
      }
    }

    const request = {
      paymentId: String(paymentInfo.paymentId),
      cancelAmount: cancelType === "full" ? String(remainingAmount) : String(partialAmount),
    };

    setBaseModal({
      isOpen: true,
      title: "결제취소 하시겠습니까?",
      onClick: () => {
        onPaymentCancel(request);
        setBaseModal({ isOpen: false });
      },
      onClose: () => setBaseModal({ isOpen: false }),
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
    });

    // onSubmit(request);
  };

  return (
    <>
      <section className="base-abstract-modal__title border-bottom-gray mb20 ">
        <div className="flex-direction-column">
          <h1 className="mb20">결제 취소</h1>
        </div>
      </section>
      <div className="px30">
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <div className="flex-center">
              <p>결제 잔액</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4"
                preformatted={true}
                contents={`전체 결제 금액을 기준으로 표시됩니다.\n취소가 있을 경우 해당 금액이 차감되어 표시됩니다.`}
              />
            </div>
          </div>
          <div className="contents-container__grid-contents">
            <div>
              <p>{numberToStringWithComma(remainingAmount) + " 원"}</p>
            </div>
          </div>
        </section>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className="required">결제 취소</p>
          </div>
          <div className="contents-container__grid-contents">
            <div className="flex-direction-column w-100">
              <div className="flex-center-start">
                <BaseRadio
                  id="cancel-full"
                  name="cancelType"
                  label={`전체취소 (${numberToStringWithComma(remainingAmount)}원)`}
                  value="full"
                  checked={cancelType === "full"}
                  onChange={() => {
                    setCancelType("full");
                    setPartialAmount("");
                    setError("");
                  }}
                />
              </div>
              <div className="flex-center-start mt10">
                <BaseRadio
                  id="cancel-partial"
                  name="cancelType"
                  label="부분취소"
                  value="partial"
                  checked={cancelType === "partial"}
                  onChange={() => {
                    setCancelType("partial");
                    setError("");
                  }}
                  className="mr10"
                />
                <BaseInput
                  autoFocus
                  placeholder="부분취소할 금액을 입력해주세요"
                  value={partialAmount}
                  errorText={error}
                  disabled={cancelType === "full"}
                  isErrorTextAbsolute
                  onChange={(value: string) => {
                    setPartialAmount(value);
                    setError("");
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="base-abstract-modal__btn-wrap">
        <BaseButton className="color-white" title="닫기" onClick={onClose} />
        <BaseButton title="확인" onClick={handleConfirm} />
      </section>
    </>
  );
};

export default CreditCardPaymentCancelModal;
